import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { formatUKDate } from "@/lib/date";
import { Header } from "@/components/Header";
import { LocalSponsorsCard } from "@/components/client/LocalSponsorsCard";
import { LocalWhatsAppCard } from "@/components/client/LocalWhatsAppCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  ShieldCheck,
  Banknote,
  Trophy,
  AlertTriangle,
  Loader2,
  CalendarDays,
  Users,
  PoundSterling,
  Download,
  CheckCircle2,
  Trash2,
  Sparkles,
  Gift,
  Award,
  Clock,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import BroadcastPanel from "@/components/admin/BroadcastPanel";
import RegistrationSponsorshipCard from "@/components/admin/RegistrationSponsorshipCard";
import BulkEmailCard from "@/components/admin/BulkEmailCard";
import EmailHealthCard from "@/components/admin/EmailHealthCard";
import GameweekBanner from "@/components/GameweekBanner";
import EmailBlockAuditCard from "@/components/admin/EmailBlockAuditCard";
import ManualPayoutsCard from "@/components/admin/ManualPayoutsCard";
import WhatsAppOptInsCard from "@/components/admin/WhatsAppOptInsCard";
import EditorResultsCard from "@/components/admin/EditorResultsCard";
import PlayersCard from "@/components/admin/PlayersCard";
import NewMatchForm from "@/components/NewMatchForm";

const PAYOUT_OPTIONS = [
  { value: "winner_all", label: "Winner takes all (100%)" },
  { value: "50_30_20", label: "Top 3 — 50% / 30% / 20%" },
  { value: "custom", label: "Custom split (1st / 2nd / 3rd, must total 100%)" },
  { value: "voucher_qr", label: "Voucher — QR code (no cash split; sponsor voucher for the top finishers)" },
];

const SECTION = "border border-zinc-200 rounded-sm bg-white";

function shortDate(iso) {
  try {
    return formatUKDate(iso, { day: "numeric", month: "short" });
  } catch {
    return iso;
  }
}

function formatPounds(pence) {
  return `£${(Number(pence || 0) / 100).toFixed(2)}`;
}

/** Convert a row of cells to a CSV line, escaping quotes and commas. */
function csvEscape(v) {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename, headers, rows) {
  const lines = [headers.map(csvEscape).join(","), ...rows.map((r) => r.map(csvEscape).join(","))];
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function ExportButton({ onClick, testId, label = "Export CSV" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-zinc-200 text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-950 hover:border-zinc-400 transition-colors"
    >
      <Download className="h-3 w-3" /> {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Stable chart-prop constants — kept module-level so recharts can bail out of
// re-rendering when data hasn't changed (inline object literals would recreate
// on every parent render).
// ---------------------------------------------------------------------------
const CHART_MARGIN = { top: 8, right: 8, left: -16, bottom: 8 };
const AXIS_TICK = { fontSize: 11, fill: "#71717A" };
const TOOLTIP_CURSOR_BLUE = { fill: "rgba(0,56,255,0.06)" };
const TOOLTIP_CURSOR_GREEN = { fill: "rgba(16,185,129,0.06)" };
const TOOLTIP_CONTENT = { borderRadius: 2, border: "1px solid #E4E4E7", fontSize: 12 };
const BAR_RADIUS = [2, 2, 0, 0];
const formatPoundTick = (v) => `£${v}`;
const formatPoundTooltip = (v) => `£${Number(v).toFixed(2)}`;

// ---------------------------------------------------------------------------
// Stats dashboard (entrants + money) over the last 12 weeks
// ---------------------------------------------------------------------------
function StatsDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/editor/stats?weeks=12");
        if (!cancelled) setStats(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Loading stats…</div>;
  }
  const data = (stats?.data || []).map((r) => ({ ...r, dateLabel: shortDate(r.date), money_pounds: r.money_pence / 100 }));
  const isEmpty = data.length === 0;

  return (
    <section className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="stats-dashboard">
      {/* iter139 — Top KPI row (match-days, total entrants, total
          collected) removed per editor request. Charts + tables below
          still render the same data, just without the trio of hero
          tiles at the top. */}

      {stats?.fee_pence ? (
      <div className={`${SECTION} p-5 lg:col-span-3`} data-testid="chart-money">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 inline-flex items-center gap-1.5">
            <PoundSterling className="h-3 w-3" /> Money collected per match-day
          </p>
          <div className="inline-flex items-center gap-3">
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">Last 12 weeks · GBP</p>
            <ExportButton
              testId="export-money-csv"
              onClick={() => {
                if (isEmpty) return;
                downloadCsv(
                  `money-collected-last-12-weeks.csv`,
                  ["date", "entrants", "fee_pence", "money_pence", "money_gbp"],
                  data.map((r) => [r.date, r.entrants, stats?.fee_pence || 0, r.money_pence, (r.money_pence / 100).toFixed(2)]),
                );
              }}
            />
          </div>
        </div>
        {isEmpty ? (
          <p className="text-sm text-zinc-500 py-12 text-center">No data yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
              <XAxis dataKey="dateLabel" tick={AXIS_TICK} tickLine={false} axisLine={false} />
              <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={formatPoundTick} />
              <Tooltip cursor={TOOLTIP_CURSOR_GREEN} contentStyle={TOOLTIP_CONTENT} formatter={formatPoundTooltip} />
              <Bar dataKey="money_pounds" fill="#10B981" radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      ) : null}
    </section>
  );
}

function KPICard({ icon: Icon, label, value, subtitle, testId }) {
  return (
    <div className={`${SECTION} p-5`} data-testid={testId}>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 inline-flex items-center gap-1.5">
        <Icon className="h-3 w-3" /> {label}
      </p>
      <p className="font-heading text-3xl font-bold tabular-nums text-zinc-950 mt-1.5">{value}</p>
      {subtitle && <p className="text-xs text-zinc-500 mt-1">{subtitle}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Unified Pool & Prize Fund + Prize Configuration
// Single form that PUTs /pool-config AND /season-prize-config in parallel.
// ---------------------------------------------------------------------------
function PoolAndPrizeSection() {
  // --- Pool state ---------------------------------------------------------
  const [poolCfg, setPoolCfg] = useState(null);
  const [feePounds, setFeePounds] = useState("0.00");
  const [potPounds, setPotPounds] = useState("0.00");
  const [seasonPounds, setSeasonPounds] = useState("0.00");

  // --- Prize state --------------------------------------------------------
  const [prizeCfg, setPrizeCfg] = useState(null);

  // --- Shared -------------------------------------------------------------
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Loaders ----------------------------------------------------------------
  const loadPool = useCallback(async () => {
    const { data } = await api.get("/pool-config");
    const normalized = { ...data };
    if (data.stripe_enabled && data.free_play_enabled) {
      normalized.stripe_enabled = false;
    } else if (!data.stripe_enabled && !data.free_play_enabled) {
      normalized.free_play_enabled = true;
    }
    setPoolCfg(normalized);
    setFeePounds((data.entry_fee_pence / 100).toFixed(2));
    setPotPounds(((data.current_prize_fund_pence || 0) / 100).toFixed(2));
    setSeasonPounds(((data.end_of_league_prize_pence || 0) / 100).toFixed(2));
  }, []);

  const loadPrize = useCallback(async () => {
    const { data } = await api.get("/season-prize-config");
    // Use ?? so an explicit `0` (bragging-rights league) survives —
    // `|| 1` would silently coerce 0 back to 1 on every load.
    const wc = data.winner_count ?? 1;
    setPrizeCfg({
      winner_count: wc,
      prize_value_type: data.prize_value_type || "text",
      prize_value_text: data.prize_value_text || "",
      prize_value_pence: data.prize_value_pence || 1000,
      prize_value_percent: data.prize_value_percent || 10,
      sponsor_name: data.sponsor_name || "",
      notes: data.notes || "",
      expires_at: data.expires_at || "",
    });
  }, []);

  useEffect(() => {
    Promise.all([loadPool(), loadPrize()]).catch((e) => toast.error(formatApiError(e)));
  }, [loadPool, loadPrize]);

  // Helpers ----------------------------------------------------------------
  const setPoolField = (patch) => setPoolCfg((c) => ({ ...c, ...patch }));
  const setPrizeField = (patch) => setPrizeCfg((c) => ({ ...c, ...patch }));

  // ---------- Save (both endpoints in parallel) ---------------------------
  const savePool = async () => {
    const customSplit = poolCfg.payout_split || { first: 50, second: 30, third: 20 };
    const customTotal =
      (customSplit.first || 0) + (customSplit.second || 0) + (customSplit.third || 0);
    const payload = {
      stripe_enabled: !!poolCfg.stripe_enabled,
      free_play_enabled: !!poolCfg.free_play_enabled,
      entry_fee_pence: Math.max(0, Math.round(parseFloat(feePounds || "0") * 100)),
      current_prize_fund_pence: Math.max(0, Math.round(parseFloat(potPounds || "0") * 100)),
      end_of_league_prize_pence:
        poolCfg.season_prize_type === "percent"
          ? 0
          : Math.max(0, Math.round(parseFloat(seasonPounds || "0") * 100)),
      // iter147 — season prize type + percent (super-admin only toggle)
      season_prize_type: poolCfg.season_prize_type === "percent" ? "percent" : "fixed",
      season_prize_percent:
        poolCfg.season_prize_type === "percent"
          ? Math.max(0, Math.min(100, Number(poolCfg.season_prize_percent) || 0))
          : 0,
      payout_mode: poolCfg.payout_mode,
      payout_split: poolCfg.payout_mode === "custom" ? customSplit : null,
    };
    if (payload.stripe_enabled && payload.entry_fee_pence <= 0) {
      throw new Error("Set an entry fee greater than £0 when paid play is enabled");
    }
    if (!payload.stripe_enabled && !payload.free_play_enabled) {
      throw new Error("Enable at least one of: paid play or free play");
    }
    if (payload.payout_mode === "custom" && customTotal !== 100) {
      throw new Error(`Custom split must total 100% (currently ${customTotal}%)`);
    }
    const { data } = await api.put("/pool-config", payload);
    setPoolCfg(data);
    setFeePounds((data.entry_fee_pence / 100).toFixed(2));
    setPotPounds(((data.current_prize_fund_pence || 0) / 100).toFixed(2));
    setSeasonPounds(((data.end_of_league_prize_pence || 0) / 100).toFixed(2));
    return data;
  };

  const savePrize = async () => {
    const payload = {
      winner_count: Number(prizeCfg.winner_count),
      prize_value_type: prizeCfg.prize_value_type,
      prize_value_text:
        prizeCfg.prize_value_type === "text"
          ? prizeCfg.prize_value_text.trim() || null
          : null,
      prize_value_pence:
        prizeCfg.prize_value_type === "currency"
          ? Number(prizeCfg.prize_value_pence)
          : null,
      prize_value_percent:
        prizeCfg.prize_value_type === "percent"
          ? Number(prizeCfg.prize_value_percent)
          : null,
      sponsor_name: prizeCfg.sponsor_name.trim() || null,
      notes: prizeCfg.notes.trim() || null,
      expires_at: prizeCfg.expires_at || null,
    };
    const { data } = await api.put("/season-prize-config", payload);
    // ?? so an explicit `0` survives (bragging-rights league).
    const wc = data.winner_count ?? 1;
    setPrizeCfg({
      winner_count: wc,
      prize_value_type: data.prize_value_type || "text",
      prize_value_text: data.prize_value_text || "",
      prize_value_pence: data.prize_value_pence || 1000,
      prize_value_percent: data.prize_value_percent || 10,
      sponsor_name: data.sponsor_name || "",
      notes: data.notes || "",
      expires_at: data.expires_at || "",
    });
    return data;
  };

  const saveAll = async () => {
    setConfirmOpen(false);
    setSaving(true);
    try {
      const results = await Promise.allSettled([savePool(), savePrize()]);
      const failed = results
        .map((r, idx) => ({ r, label: idx === 0 ? "Pool" : "Prize" }))
        .filter(({ r }) => r.status === "rejected");
      if (failed.length === 0) {
        toast.success("Pool & prize configuration saved.");
      } else {
        const msgs = failed.map(({ r, label }) => `${label}: ${formatApiError(r.reason)}`);
        toast.error(msgs.join("  •  "));
      }
    } finally {
      setSaving(false);
    }
  };

  if (!poolCfg || !prizeCfg) {
    return (
      <div className={`${SECTION} p-6 text-xs uppercase tracking-[0.2em] text-zinc-500`}>
        Loading pool &amp; prize configuration…
      </div>
    );
  }

  const customSplit = poolCfg.payout_split || { first: 50, second: 30, third: 20 };
  const customTotal =
    (customSplit.first || 0) + (customSplit.second || 0) + (customSplit.third || 0);
  const setCustom = (key, val) =>
    setPoolField({ payout_split: { ...customSplit, [key]: Number(val) || 0 } });

  const previewValue =
    prizeCfg.prize_value_type === "currency"
      ? (() => {
          const pence = Number(prizeCfg.prize_value_pence) || 0;
          const pounds = pence / 100;
          return pence % 100 === 0
            ? `£${pounds.toFixed(0)} voucher`
            : `£${pounds.toFixed(2)} voucher`;
        })()
      : prizeCfg.prize_value_type === "percent"
      ? `${Number(prizeCfg.prize_value_percent) || 0}% off`
      : prizeCfg.prize_value_text || "—";

  const voucherActive = poolCfg.payout_mode === "voucher_qr";

  return (
    <section
      className={`${SECTION} p-6`}
      data-testid="pool-and-prize-section"
    >
      <div className="flex items-center gap-2 mb-5">
        <Banknote className="h-4 w-4 text-zinc-500" />
        <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-950">
          Fees and Prize Fund
        </h2>
        {poolCfg.stripe_enabled && (
          <span className="px-2 py-1 rounded-sm bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-[0.15em]">
            LEAGUE PAID
          </span>
        )}
        {voucherActive && (
          <span className="px-2 py-1 rounded-sm bg-amber-50 text-amber-700 text-[10px] font-bold uppercase tracking-[0.15em]">
            VOUCHER PRIZE
          </span>
        )}
      </div>
      <p className="text-sm text-zinc-500 mb-6 max-w-2xl">
        Toggle Stripe payments on/off and set your <strong>League entry fee</strong> — one payment unlocks every
        match in the active season. Prize fund and payout split still apply league-wide.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left column: Play mode + fees + funds + vouchers awarded */}
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
              Play mode
            </Label>
            <div className="mt-2 space-y-2">
              <label
                className={`flex items-start gap-3 p-3 border rounded-sm cursor-pointer ${
                  poolCfg.free_play_enabled
                    ? "border-zinc-950 bg-zinc-50"
                    : "border-zinc-200 hover:border-zinc-400"
                }`}
              >
                <input
                  type="radio"
                  name="play_mode"
                  className="mt-1"
                  checked={poolCfg.free_play_enabled}
                  onChange={() =>
                    setPoolField({ free_play_enabled: true, stripe_enabled: false })
                  }
                  data-testid="mode-free"
                />
                <span>
                  <span className="text-sm font-semibold text-zinc-950 block">Free play</span>
                  <span className="text-xs text-zinc-500">
                    Players predict without paying. No entry fee, no money flows.
                  </span>
                </span>
              </label>
              <label
                className={`flex items-start gap-3 p-3 border rounded-sm cursor-pointer ${
                  poolCfg.stripe_enabled
                    ? "border-zinc-950 bg-zinc-50"
                    : "border-zinc-200 hover:border-zinc-400"
                }`}
              >
                <input
                  type="radio"
                  name="play_mode"
                  className="mt-1"
                  checked={poolCfg.stripe_enabled}
                  onChange={() =>
                    setPoolField({ free_play_enabled: false, stripe_enabled: true })
                  }
                  data-testid="mode-paid"
                />
                <span>
                  <span className="text-sm font-semibold text-zinc-950 block">
                    Paid entry via Stripe
                  </span>
                  <span className="text-xs text-zinc-500">
                    Players pay ONE fee that unlocks every match in the active league. Season boundary resets access.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div>
            <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
              League entry fee (£)
            </Label>
            <div className="relative mt-2">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">£</span>
              <Input
                type="number"
                min={0}
                step="0.50"
                value={feePounds}
                disabled={!poolCfg.stripe_enabled}
                onChange={(e) => setFeePounds(e.target.value)}
                data-testid="fee-input"
                className="h-11 pl-7 rounded-sm border-zinc-300 disabled:bg-zinc-50"
              />
            </div>
          </div>

          {!voucherActive && (
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                Current match-day prize fund (£)
              </Label>
              <div className="relative mt-2">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">£</span>
                <Input
                  type="number"
                  min={0}
                  step="1"
                  value={potPounds}
                  onChange={(e) => setPotPounds(e.target.value)}
                  data-testid="current-prize-input"
                  className="h-11 pl-7 rounded-sm border-zinc-300"
                />
              </div>
              <p className="text-[11px] text-zinc-500 mt-1.5">
                Amount shown to players on the Predictions page for today&apos;s match-day.
              </p>
            </div>
          )}

          {!voucherActive && (
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                Total prize fund for overall league
              </Label>
              {/* iter147 — Super-admin choice: fixed £ vs % of collected
                  entry fees. Percent only makes sense with paid play,
                  but we surface both radios always so the choice sticks
                  even if Stripe is temporarily toggled off. */}
              <div className="mt-2 grid grid-cols-2 gap-2" data-testid="season-prize-type-toggle">
                {[
                  { value: "fixed", label: "Fixed amount (£)" },
                  { value: "percent", label: "% of entry fees" },
                ].map((opt) => {
                  const active = (poolCfg.season_prize_type || "fixed") === opt.value;
                  return (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-2 p-2.5 border rounded-sm cursor-pointer text-sm ${
                        active
                          ? "border-zinc-950 bg-zinc-50 font-semibold text-zinc-950"
                          : "border-zinc-200 hover:border-zinc-400 text-zinc-700"
                      }`}
                    >
                      <input
                        type="radio"
                        name="season_prize_type"
                        checked={active}
                        onChange={() => setPoolField({ season_prize_type: opt.value })}
                        data-testid={`season-prize-type-${opt.value}`}
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>

              {(poolCfg.season_prize_type || "fixed") === "fixed" ? (
                <>
                  <div className="relative mt-3">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">£</span>
                    <Input
                      type="number"
                      min={0}
                      step="1"
                      value={seasonPounds}
                      onChange={(e) => setSeasonPounds(e.target.value)}
                      data-testid="end-of-league-prize-input"
                      className="h-11 pl-7 rounded-sm border-zinc-300"
                    />
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-1.5">
                    The full pot is paid out at season close, apportioned across the top finishers
                    per the <strong>Prize split</strong> below.
                  </p>
                </>
              ) : (
                <>
                  <div className="relative mt-3">
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step="1"
                      value={poolCfg.season_prize_percent ?? 0}
                      onChange={(e) => {
                        // iter147 fix — same pattern as vouchers-awarded
                        // input (iter144): controlled type=number in React
                        // skips DOM sync when parsed value matches state,
                        // so "0" → typing "5" leaves the leading zero
                        // visible. Imperatively normalise the DOM string.
                        const raw = parseInt(e.target.value, 10);
                        const clamped = Number.isNaN(raw)
                          ? 0
                          : Math.min(100, Math.max(0, raw));
                        const normalised = e.target.value === "" ? "" : String(clamped);
                        if (e.target.value !== normalised) {
                          e.target.value = normalised;
                        }
                        setPoolField({ season_prize_percent: clamped });
                      }}
                      data-testid="season-prize-percent-input"
                      className="h-11 pr-8 rounded-sm border-zinc-300"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-1.5">
                    Pot grows automatically with paid entries — players see the live £ value
                    with a <em>&quot;({poolCfg.season_prize_percent || 0}% of entry fees)&quot;</em> sub-label.
                    Requires paid entry mode.
                  </p>
                </>
              )}
            </div>
          )}

          {voucherActive && (
            <div
              className="rounded-sm border border-amber-200 bg-amber-50/60 p-3"
              data-testid="voucher-mode-notice"
            >
              <p className="text-[11px] font-semibold text-amber-900 leading-relaxed">
                <Gift className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
                Voucher — QR code mode is on, so cash prize-fund fields are hidden. Set the
                voucher details on the right and confirm the number of vouchers below.
              </p>
            </div>
          )}

          {/* Number of vouchers awarded */}
          <div data-testid="vouchers-awarded-block">
            <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 inline-flex items-center gap-1.5">
              <Gift className="h-3 w-3" /> Number of vouchers awarded
            </Label>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <Input
                type="number"
                min={0}
                max={999}
                value={prizeCfg.winner_count}
                onChange={(e) => {
                  // iter140 — allow 0 (bragging-rights league) and cap
                  // at 999 so the prize-split UI can never overflow.
                  // iter144 — also strip leading zeros from the DOM.
                  // React's controlled `type=number` input skips DOM
                  // sync when the PARSED value matches state (e.g.
                  // typing "0100" → parseInt→100 === state 100 → React
                  // no-ops, leaving "0100" visible in the box). We
                  // imperatively normalise the input's own string.
                  const raw = parseInt(e.target.value, 10);
                  const clamped = Number.isNaN(raw)
                    ? 0
                    : Math.min(999, Math.max(0, raw));
                  const normalised = e.target.value === "" ? "" : String(clamped);
                  if (e.target.value !== normalised) {
                    e.target.value = normalised;
                  }
                  setPrizeField({ winner_count: clamped });
                }}
                data-testid="vouchers-awarded-input"
                className="h-11 w-32 rounded-sm border-zinc-300 tabular-nums text-center text-lg font-heading font-bold"
              />
              <span className="text-sm text-zinc-500">
                {prizeCfg.winner_count === 0
                  ? "No vouchers — bragging-rights league only."
                  : (
                    <>
                      Each of the top {prizeCfg.winner_count} finisher
                      {prizeCfg.winner_count === 1 ? "" : "s"} gets a voucher.
                    </>
                  )}
              </span>
            </div>
            <p className="mt-1.5 text-[11px] text-zinc-500">
              Enter any number from 0 to 999. You&apos;ll get a chance to
              review this on the Save confirmation.
            </p>
          </div>
        </div>

        {/* Right column: Prize split (with new voucher-QR option) + voucher details */}
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
            Prize split
          </Label>
          <div className="mt-2 space-y-2">
            {PAYOUT_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-start gap-3 p-3 border rounded-sm cursor-pointer ${
                  poolCfg.payout_mode === opt.value
                    ? "border-zinc-950 bg-zinc-50"
                    : "border-zinc-200 hover:border-zinc-400"
                }`}
              >
                <input
                  type="radio"
                  name="payout_mode"
                  className="mt-1"
                  checked={poolCfg.payout_mode === opt.value}
                  onChange={() => setPoolField({ payout_mode: opt.value })}
                  data-testid={`mode-${opt.value}`}
                />
                <span className="text-sm text-zinc-800 leading-snug">{opt.label}</span>
              </label>
            ))}
          </div>

          {poolCfg.payout_mode === "custom" && (
            <div className="mt-4 grid grid-cols-3 gap-3" data-testid="custom-split">
              {["first", "second", "third"].map((pos) => (
                <div key={pos}>
                  <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 capitalize">
                    {pos}
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={customSplit[pos] ?? 0}
                      onChange={(e) => setCustom(pos, e.target.value)}
                      data-testid={`custom-${pos}`}
                      className="h-10 pr-7 text-right rounded-sm border-zinc-300"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">
                      %
                    </span>
                  </div>
                </div>
              ))}
              <p
                className={`col-span-3 text-xs ${
                  customTotal === 100 ? "text-emerald-600" : "text-rose-600"
                }`}
              >
                Total: {customTotal}% {customTotal === 100 ? "✓" : "(must equal 100%)"}
              </p>
            </div>
          )}

          {/* Voucher configuration — below the split, highlighted when active */}
          <div
            className={`mt-6 rounded-sm border p-4 space-y-5 ${
              voucherActive ? "border-amber-300 bg-amber-50/40" : "border-zinc-200 bg-zinc-50/40"
            }`}
            data-testid="voucher-config-block"
          >
            <div className="flex items-start gap-2">
              <Gift className="h-4 w-4 text-amber-600 mt-0.5" />
              <div>
                <p className="font-heading font-bold text-sm text-zinc-950">
                  Voucher configuration
                </p>
                <p className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">
                  Applied at season close.
                  {voucherActive
                    ? " These vouchers replace any cash split — winners scan a QR code at the bar."
                    : " Vouchers are still issued alongside the cash split you picked above."}
                </p>
              </div>
            </div>

            {/* Voucher value ----------------------------------------- */}
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                Voucher value
              </Label>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setPrizeField({ prize_value_type: "text" })}
                  data-testid="prize-value-type-text"
                  className={`h-9 px-3 rounded-sm border-2 text-xs font-semibold uppercase tracking-wider ${
                    prizeCfg.prize_value_type === "text"
                      ? "border-zinc-950 bg-zinc-950 text-white"
                      : "border-zinc-300 text-zinc-700"
                  }`}
                >
                  Free text
                </button>
                <button
                  type="button"
                  onClick={() => setPrizeField({ prize_value_type: "currency" })}
                  data-testid="prize-value-type-currency"
                  className={`h-9 px-3 rounded-sm border-2 text-xs font-semibold uppercase tracking-wider ${
                    prizeCfg.prize_value_type === "currency"
                      ? "border-zinc-950 bg-zinc-950 text-white"
                      : "border-zinc-300 text-zinc-700"
                  }`}
                >
                  Currency (£)
                </button>
                <button
                  type="button"
                  onClick={() => setPrizeField({ prize_value_type: "percent" })}
                  data-testid="prize-value-type-percent"
                  className={`h-9 px-3 rounded-sm border-2 text-xs font-semibold uppercase tracking-wider ${
                    prizeCfg.prize_value_type === "percent"
                      ? "border-zinc-950 bg-zinc-950 text-white"
                      : "border-zinc-300 text-zinc-700"
                  }`}
                >
                  % discount
                </button>
              </div>

              {prizeCfg.prize_value_type === "text" ? (
                <div className="mt-2.5">
                  <Input
                    value={prizeCfg.prize_value_text}
                    onChange={(e) =>
                      setPrizeField({ prize_value_text: e.target.value.slice(0, 120) })
                    }
                    placeholder="e.g. Free pint at The Founders Arms"
                    data-testid="prize-value-text"
                    className="h-10 rounded-sm border-zinc-300"
                  />
                  <p className="mt-1 text-[11px] text-zinc-400">
                    Shown verbatim on the winner&apos;s voucher. Max 120 chars.
                  </p>
                </div>
              ) : prizeCfg.prize_value_type === "currency" ? (
                <div className="mt-2.5 max-w-xs">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">£</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={(Number(prizeCfg.prize_value_pence) / 100).toFixed(2)}
                      onChange={(e) =>
                        setPrizeField({
                          prize_value_pence: Math.max(
                            0,
                            Math.round(parseFloat(e.target.value || "0") * 100)
                          ),
                        })
                      }
                      data-testid="prize-value-currency"
                      className="h-10 pl-7 rounded-sm border-zinc-300 tabular-nums"
                    />
                  </div>
                  <p className="mt-1 text-[11px] text-zinc-400">
                    Shows as &ldquo;{previewValue}&rdquo; on the voucher.
                  </p>
                </div>
              ) : (
                <div className="mt-2.5 max-w-xs">
                  <div className="relative">
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      step={1}
                      value={prizeCfg.prize_value_percent}
                      onChange={(e) =>
                        setPrizeField({
                          prize_value_percent: Math.min(
                            100,
                            Math.max(1, parseInt(e.target.value || "1", 10) || 1)
                          ),
                        })
                      }
                      data-testid="prize-value-percent"
                      className="h-10 pr-7 rounded-sm border-zinc-300 tabular-nums text-right"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                  </div>
                  <p className="mt-1 text-[11px] text-zinc-400">
                    Shows as &ldquo;{previewValue}&rdquo; on the voucher.
                  </p>
                </div>
              )}
            </div>

            {/* Expiry ---------------------------------------------- */}
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 inline-flex items-center gap-1.5">
                <Clock className="h-3 w-3" /> Expiry date (optional)
              </Label>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <Input
                  type="date"
                  value={prizeCfg.expires_at || ""}
                  onChange={(e) => setPrizeField({ expires_at: e.target.value || "" })}
                  data-testid="prize-expiry-input"
                  className="h-10 rounded-sm border-zinc-300 max-w-[220px]"
                />
                {prizeCfg.expires_at ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setPrizeField({ expires_at: "" })}
                    data-testid="prize-expiry-clear"
                    className="h-10 rounded-sm border-zinc-300 text-xs"
                  >
                    Clear
                  </Button>
                ) : null}
              </div>
              <p className="mt-1 text-[11px] text-zinc-400">
                After this date the QR code will be rejected at the bar. Leave blank for no expiry.
              </p>
            </div>

            {/* Sponsor + notes ------------------------------------- */}
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                Sponsor name (optional)
              </Label>
              <Input
                value={prizeCfg.sponsor_name}
                onChange={(e) =>
                  setPrizeField({ sponsor_name: e.target.value.slice(0, 120) })
                }
                placeholder="e.g. The Founders Arms"
                data-testid="prize-sponsor-name"
                className="mt-2 h-10 rounded-sm border-zinc-300"
              />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                Redemption instructions (optional)
              </Label>
              <Textarea
                value={prizeCfg.notes}
                onChange={(e) => setPrizeField({ notes: e.target.value.slice(0, 400) })}
                placeholder="e.g. Show this QR code at the bar to claim your prize."
                data-testid="prize-notes"
                className="mt-2 min-h-[80px] rounded-sm border-zinc-300"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Preview + Save all ------------------------------------------------ */}
      <div className="mt-8 pt-5 border-t border-zinc-100 flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs text-zinc-500" data-testid="prize-preview">
          <p className="text-[10px] uppercase tracking-[0.18em] mb-1">Voucher preview</p>
          <p className="text-zinc-950">
            <span className="font-heading font-bold">
              {prizeCfg.winner_count === 1 ? "Champion" : `Top ${prizeCfg.winner_count}`}
            </span>{" "}
            <Award className="inline h-3.5 w-3.5" /> each get:{" "}
            <span className="font-heading font-bold">{previewValue}</span>
            {prizeCfg.sponsor_name ? (
              <>
                {" "}
                · <em>{prizeCfg.sponsor_name}</em>
              </>
            ) : null}
            {prizeCfg.expires_at ? (
              <>
                {" "}
                ·{" "}
                <span className="text-rose-700">
                  expires {new Date(prizeCfg.expires_at).toLocaleDateString("en-GB")}
                </span>
              </>
            ) : null}
          </p>
        </div>
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={saving}
          data-testid="save-pool-and-prize"
          className="h-11 px-8 rounded-sm bg-zinc-950 hover:bg-zinc-900 text-white font-heading font-bold text-sm uppercase tracking-[0.14em] disabled:bg-zinc-400 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving all…
            </>
          ) : (
            "Save all (pool + prize)"
          )}
        </Button>
      </div>

      {/* Confirm dialog ------------------------------------------------- */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="save-confirm-dialog" className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Confirm pool &amp; prize configuration</DialogTitle>
            <DialogDescription>
              Please review before saving. This overwrites the previous configuration and
              applies at the next season close.
            </DialogDescription>
          </DialogHeader>

          <ul className="text-sm text-zinc-700 space-y-2 mt-2">
            <li>
              <strong>Play mode:</strong>{" "}
              {poolCfg.stripe_enabled
                ? `Paid entry via Stripe (£${(
                    Math.max(0, Math.round(parseFloat(feePounds || "0") * 100)) / 100
                  ).toFixed(2)} per match-day)`
                : "Free play"}
            </li>
            <li>
              <strong>Prize split:</strong>{" "}
              {(PAYOUT_OPTIONS.find((o) => o.value === poolCfg.payout_mode) || {}).label ||
                poolCfg.payout_mode}
            </li>
            {!voucherActive && (
              <li>
                <strong>Total prize fund:</strong> £{parseFloat(seasonPounds || "0").toFixed(2)}
              </li>
            )}
            <li data-testid="confirm-vouchers-line">
              <strong>Vouchers being issued:</strong>{" "}
              <span className="font-heading font-bold text-zinc-950 text-lg">
                {prizeCfg.winner_count}
              </span>{" "}
              at season close · <em>{previewValue}</em>
              {prizeCfg.sponsor_name ? <> · {prizeCfg.sponsor_name}</> : null}
              {prizeCfg.expires_at ? (
                <>
                  {" "}
                  · expires {new Date(prizeCfg.expires_at).toLocaleDateString("en-GB")}
                </>
              ) : null}
            </li>
          </ul>

          <DialogFooter className="mt-2 gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              data-testid="save-confirm-cancel"
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={saveAll}
              disabled={saving}
              data-testid="save-confirm-btn"
              className="rounded-sm bg-zinc-950 hover:bg-zinc-900 text-white font-heading font-bold"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving…
                </>
              ) : (
                <>Confirm &amp; save</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Match-day scheduling (move match dates around)
// ---------------------------------------------------------------------------
function MatchDayList({ refreshKey = 0 }) {
  const [days, setDays] = useState([]);
  const [matchesByDate, setMatchesByDate] = useState({});
  const [openDate, setOpenDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // iter139 — inline result editing: which match's actual-result panel is
  // expanded. Only one open at a time to keep the layout compact.
  const [openResultId, setOpenResultId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pools");
      setDays(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const toggle = async (date) => {
    if (openDate === date) {
      setOpenDate(null);
      return;
    }
    setOpenDate(date);
    if (!matchesByDate[date]) {
      try {
        const { data } = await api.get(`/matches?date=${date}`);
        setMatchesByDate((m) => ({ ...m, [date]: data || [] }));
      } catch (e) {
        toast.error(formatApiError(e));
      }
    }
  };

  const move = async (m, newDate) => {
    if (!newDate || newDate === m.match_date) return;
    setBusyId(m.id);
    try {
      await api.patch(`/matches/${m.id}`, { match_date: newDate });
      toast.success(`${m.home_team} vs ${m.away_team} moved to ${newDate}`);
      // Remove from old date; refresh list
      setMatchesByDate((map) => ({
        ...map,
        [m.match_date]: (map[m.match_date] || []).filter((x) => x.id !== m.id),
      }));
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const changeKickoff = async (m, newKickoff) => {
    const k = (newKickoff || "").trim();
    if (!k || k === m.kickoff) return;
    setBusyId(m.id);
    try {
      await api.patch(`/matches/${m.id}`, { kickoff: k });
      toast.success(`${m.home_team} vs ${m.away_team} kickoff set to ${k}`);
      setMatchesByDate((map) => ({
        ...map,
        [m.match_date]: (map[m.match_date] || []).map((x) =>
          x.id === m.id ? { ...x, kickoff: k } : x,
        ),
      }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const removeMatch = async (m) => {
    const ok = window.confirm(
      `Delete ${m.home_team} vs ${m.away_team} on ${m.match_date}? This will permanently remove the fixture and any predictions attached to it. Cannot be undone.`,
    );
    if (!ok) return;
    setBusyId(m.id);
    try {
      await api.delete(`/matches/${m.id}?hard=true`);
      toast.success(`${m.home_team} vs ${m.away_team} deleted`);
      setMatchesByDate((map) => ({
        ...map,
        [m.match_date]: (map[m.match_date] || []).filter((x) => x.id !== m.id),
      }));
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  // iter139 — save (or overwrite) the actual result for a match. Uses the
  // existing `POST /matches/{id}/result` endpoint which:
  //   - persists match.actual + status="finished",
  //   - re-scores every prediction attached to this match (so previously
  //     awarded points update immediately for every /slug that had this
  //     match in its selected_ids),
  //   - fires the Daily League Update email if this was the final match
  //     of its match-day (deduped by season+date+pub_id per iter113).
  const saveResult = async (m, form) => {
    setBusyId(m.id);
    try {
      const payload = {
        goals: Number(form.goals) || 0,
        shots_on_target: Number(form.shots_on_target) || 0,
        corners: Number(form.corners) || 0,
        offsides: Number(form.offsides) || 0,
        throw_ins: Number(form.throw_ins) || 0,
        fouls: Number(form.fouls) || 0,
      };
      const { data: updated } = await api.post(
        `/matches/${m.id}/result`,
        payload,
      );
      toast.success(
        `Result saved for ${m.home_team} vs ${m.away_team} — predictions re-scored across every pub`,
      );
      setMatchesByDate((map) => ({
        ...map,
        [m.match_date]: (map[m.match_date] || []).map((x) =>
          x.id === m.id ? { ...x, ...updated } : x,
        ),
      }));
      setOpenResultId(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className={`${SECTION} p-6`} data-testid="matchday-list">
      <div className="flex items-center gap-2 mb-5">
        <CalendarDays className="h-4 w-4 text-zinc-500" />
        <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-950">Match-day scheduling</h2>
      </div>
      <p className="text-sm text-zinc-500 mb-4 max-w-2xl">
        Click a match-day to expand it. You can change a match&apos;s <strong>date</strong>, edit its <strong>kickoff time</strong>,
        enter or <strong>overwrite the actual result</strong>, or <strong>delete it entirely</strong>. Result edits re-score
        every prediction across every pub that uses this match. Predictions follow the match across date moves; deleting also
        removes every prediction submitted on that match. Imported fixtures show a small <em>Imported</em> badge.
      </p>
      {loading ? (
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Loading…</p>
      ) : days.length === 0 ? (
        <p className="text-sm text-zinc-500">No match-days yet — add fixtures from the Admin page.</p>
      ) : (
        <div className="divide-y divide-zinc-100 border border-zinc-200 rounded-sm">
          {days.map((d) => {
            const isOpen = openDate === d.match_date;
            const matches = matchesByDate[d.match_date] || [];
            return (
              <div key={d.match_date} data-testid={`matchday-row-${d.match_date}`}>
                <button
                  onClick={() => toggle(d.match_date)}
                  data-testid={`matchday-toggle-${d.match_date}`}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-zinc-50"
                >
                  <span className="font-semibold text-zinc-950">
                    {formatUKDate(d.match_date, {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                  <span className="text-xs uppercase tracking-[0.15em] text-zinc-500">
                    {d.match_count} {d.match_count === 1 ? "match" : "matches"}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-4 py-3 bg-zinc-50 space-y-2" data-testid={`matchday-detail-${d.match_date}`}>
                    {matches.length === 0 ? (
                      <p className="text-xs text-zinc-500">No matches on this date.</p>
                    ) : (
                      matches.map((m) => {
                        const isResultOpen = openResultId === m.id;
                        const hasResult = m.status === "finished" && m.actual;
                        return (
                        <div
                          key={m.id}
                          data-testid={`matchday-match-wrapper-${m.id}`}
                          className="rounded-sm bg-white border border-zinc-200"
                        >
                        <div
                          data-testid={`matchday-match-row-${m.id}`}
                          className="flex items-center gap-3 flex-wrap p-3"
                        >
                          <Input
                            type="time"
                            defaultValue={m.kickoff || ""}
                            onBlur={(e) => changeKickoff(m, e.target.value)}
                            disabled={busyId === m.id}
                            data-testid={`scheduler-kickoff-${m.id}`}
                            title="Kick-off time (24h)"
                            className="h-9 w-28 rounded-sm border-zinc-300 text-sm font-mono tabular-nums"
                          />
                          <span className="text-sm font-semibold text-zinc-950 flex-1 min-w-[160px]">
                            {m.home_team} <span className="text-zinc-400">vs</span> {m.away_team}
                          </span>
                          {m.imported_from === "live-score-api" && (
                            <span
                              data-testid={`scheduler-imported-badge-${m.id}`}
                              title={`Imported from live-score-api${m.competition_label ? ` · ${m.competition_label}` : ""}`}
                              className="inline-flex items-center gap-1 h-6 px-2 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-[10px] font-semibold uppercase tracking-wider"
                            >
                              <Download className="h-3 w-3" /> Imported
                            </span>
                          )}
                          <Input
                            type="date"
                            defaultValue={m.match_date}
                            onChange={(e) => move(m, e.target.value)}
                            disabled={busyId === m.id}
                            data-testid={`scheduler-date-${m.id}`}
                            className="h-9 w-44 rounded-sm border-zinc-300 text-sm"
                          />
                          {/* iter139 — inline result edit toggle. Shows a
                              green "Edit result" pill when the match is
                              already finished (so the admin knows they're
                              overwriting), otherwise a neutral outline. */}
                          <Button
                            type="button"
                            onClick={() => setOpenResultId(isResultOpen ? null : m.id)}
                            disabled={busyId === m.id}
                            data-testid={`scheduler-result-toggle-${m.id}`}
                            title={hasResult ? "Edit or overwrite the actual result" : "Enter the actual result"}
                            className={
                              hasResult
                                ? "h-9 px-3 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
                                : "h-9 px-3 rounded-sm border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-950 text-xs font-semibold"
                            }
                          >
                            {hasResult ? (isResultOpen ? "Close result" : "Edit result") : (isResultOpen ? "Cancel" : "Enter result")}
                          </Button>
                          <Button
                            type="button"
                            onClick={() => removeMatch(m)}
                            disabled={busyId === m.id}
                            data-testid={`scheduler-delete-${m.id}`}
                            variant="outline"
                            title="Delete this fixture (removes the match and its predictions permanently)"
                            className="h-9 px-2 rounded-sm border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                          {busyId === m.id && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-500" />}
                        </div>
                        {isResultOpen && (
                          <MatchResultEditor
                            match={m}
                            busy={busyId === m.id}
                            onSave={(form) => saveResult(m, form)}
                            onCancel={() => setOpenResultId(null)}
                          />
                        )}
                        </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Inline actual-result editor for a single match (iter139). Renders below
// its match row inside "Match-day scheduling". Six numeric fields, each
// clamped to 0-99 per the backend `StatLine` model. Prefills with the
// stored `actual` values so an admin can "overwrite" a previously-saved
// result and every prediction across every pub gets re-scored server-side.
// ---------------------------------------------------------------------------
const RESULT_FIELDS = [
  { key: "goals",           label: "Goals" },
  { key: "shots_on_target", label: "Shots on target" },
  { key: "corners",         label: "Corners" },
  { key: "offsides",        label: "Offsides" },
  { key: "throw_ins",       label: "Throw-ins" },
  { key: "fouls",           label: "Fouls" },
];

function MatchResultEditor({ match, busy, onSave, onCancel }) {
  const initial = useMemo(() => {
    const a = match.actual || {};
    return RESULT_FIELDS.reduce((acc, f) => {
      acc[f.key] = a[f.key] != null ? String(a[f.key]) : "0";
      return acc;
    }, {});
  }, [match.actual, match.id]);
  const [form, setForm] = useState(initial);

  // Reset form when a different match's editor is opened.
  useEffect(() => { setForm(initial); }, [initial]);

  const setField = (key, value) => {
    const num = Math.max(0, Math.min(99, Number(value) || 0));
    setForm((f) => ({ ...f, [key]: String(num) }));
  };

  const alreadyFinished = match.status === "finished" && match.actual;

  return (
    <div
      data-testid={`match-result-editor-${match.id}`}
      className="border-t border-zinc-200 p-4 bg-zinc-50"
    >
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
          Actual result
        </p>
        {alreadyFinished && (
          <span className="inline-flex items-center h-5 px-2 rounded-sm bg-emerald-100 text-emerald-800 text-[10px] font-semibold uppercase tracking-wider">
            Already recorded · save to overwrite
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {RESULT_FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
              {f.label}
            </Label>
            <Input
              type="number"
              min={0}
              max={99}
              step="1"
              value={form[f.key]}
              onChange={(e) => setField(f.key, e.target.value)}
              disabled={busy}
              data-testid={`match-result-input-${match.id}-${f.key}`}
              className="mt-1 h-9 rounded-sm border-zinc-300 text-sm font-mono tabular-nums text-center"
            />
          </div>
        ))}
      </div>
      <p className="text-[11px] text-zinc-500 mt-3">
        Saving writes the actual result to this fixture and re-scores every
        prediction attached to it — across every pub whose match-day
        selection includes this fixture. This is the same source of truth
        the winners email and payouts use.
      </p>
      <div className="mt-3 flex items-center gap-2 justify-end">
        <Button
          type="button"
          onClick={onCancel}
          disabled={busy}
          variant="outline"
          data-testid={`match-result-cancel-${match.id}`}
          className="h-9 rounded-sm px-3 text-xs"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={() => onSave(form)}
          disabled={busy}
          data-testid={`match-result-save-${match.id}`}
          className="h-9 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-4"
        >
          {busy ? "Saving…" : (alreadyFinished ? "Overwrite result" : "Save result")}
        </Button>
      </div>
    </div>
  );
}



// ---------------------------------------------------------------------------
// Close-season card — ONLY shows when there's an active season ready to be
// closed. The "start the next season" UI lives in its own card below so the
// two operations are clearly distinct.
//
// iter167 — Multi-select close. On the master `/editor` (super-admin view,
// no `slug` prop) the confirm dialog now shows a checkbox list of every pub
// so the admin can close a subset — a partial close leaves the global
// season active and un-selected pubs keep predicting. On `/c/[slug]/editor`
// the card behaves exactly as before (single-click close of everything the
// caller has scope over).
// ---------------------------------------------------------------------------
function CloseSeasonCard({ activeSeason, onChanged, slug = null }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);

  // iter167 — pub list is only fetched on the master editor (no slug)
  const isMaster = !slug;
  const [pubs, setPubs] = useState([]);
  const [pubsLoading, setPubsLoading] = useState(false);
  // iter168 — per-pub unfinished-fixtures count, keyed by pub id.
  const [readinessByPubId, setReadinessByPubId] = useState({});
  // Set of selected pub ids. Defaults to "all selected" so hitting Close
  // without touching the list matches legacy behaviour exactly.
  const [selectedPubIds, setSelectedPubIds] = useState(() => new Set());
  const [pubsInitialised, setPubsInitialised] = useState(false);

  useEffect(() => {
    if (!isMaster || !confirmOpen || pubsInitialised) return;
    let cancelled = false;
    (async () => {
      setPubsLoading(true);
      try {
        // iter168 — fetch pubs + per-pub readiness in parallel so the
        // amber "N unfinished" chip renders in the same paint as the
        // checkbox list. Readiness failure is non-fatal (chip just
        // doesn't appear).
        const [pubsRes, readinessRes] = await Promise.all([
          api.get("/admin/assignable-pubs"),
          api.get("/admin/close-season/readiness").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const list = Array.isArray(pubsRes.data) ? pubsRes.data : [];
        setPubs(list);
        setSelectedPubIds(new Set(list.map((p) => p.id)));
        const readinessMap = {};
        for (const r of Array.isArray(readinessRes.data) ? readinessRes.data : []) {
          if (r?.id) readinessMap[r.id] = r;
        }
        setReadinessByPubId(readinessMap);
        setPubsInitialised(true);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err) || "Failed to load pubs list");
      } finally {
        if (!cancelled) setPubsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isMaster, confirmOpen, pubsInitialised]);

  const allSelected = isMaster && pubs.length > 0 && selectedPubIds.size === pubs.length;
  const noneSelected = isMaster && pubs.length > 0 && selectedPubIds.size === 0;
  // iter168 — how many currently-ticked pubs still have unfinished
  // fixtures? Used for a "You're about to close pubs mid-Gameweek"
  // warning banner + a quick-action to untick them all.
  const tickedUnfinishedCount = useMemo(() => {
    if (!isMaster) return 0;
    let n = 0;
    for (const pid of selectedPubIds) {
      if ((readinessByPubId[pid]?.unfinished_count || 0) > 0) n += 1;
    }
    return n;
  }, [isMaster, selectedPubIds, readinessByPubId]);

  const togglePub = (pubId) => {
    setSelectedPubIds((prev) => {
      const next = new Set(prev);
      if (next.has(pubId)) next.delete(pubId); else next.add(pubId);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedPubIds((prev) =>
      prev.size === pubs.length ? new Set() : new Set(pubs.map((p) => p.id))
    );
  };
  // iter168 — untick every pub that still has unfinished fixtures. Safer
  // default before hitting Close.
  const uncheckUnfinished = () => {
    setSelectedPubIds((prev) => {
      const next = new Set(prev);
      for (const pid of Array.from(next)) {
        if ((readinessByPubId[pid]?.unfinished_count || 0) > 0) next.delete(pid);
      }
      return next;
    });
  };

  const close = async () => {
    if (isMaster && pubs.length > 0 && noneSelected) {
      toast.error("Pick at least one pub to close — or tick 'Select all' to close every pub.");
      return;
    }
    setBusy(true);
    try {
      // iter167 — send `pub_ids` ONLY on a partial subset. When every
      // pub is ticked (or per-slug editor) we send no body so backend
      // takes the legacy close-everything path (also flips the season
      // doc to closed so the admin can start a new one).
      const body = isMaster && !allSelected
        ? { pub_ids: Array.from(selectedPubIds) }
        : undefined;
      const { data } = await api.post("/pools/close-season", body);
      const champ = data?.champion;
      const closedName = data?.closure?.season_name || "The season";
      const closedDate = data?.closure?.closed_at
        ? new Date(data.closure.closed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
        : "today";
      const payoutsCount = (data?.payouts_created || []).length;
      const payoutSuffix = payoutsCount > 0 ? ` ${payoutsCount} payout${payoutsCount === 1 ? "" : "s"} drafted in Manual payouts.` : "";
      const blastSent = data?.season_end_blast?.sent || 0;
      const blastSuffix = blastSent > 0 ? ` ${blastSent} recap email${blastSent === 1 ? "" : "s"} sent to players.` : "";
      if (data?.partial_close) {
        const closedCount = (data?.closed_pub_ids || []).length;
        toast.success(
          `Closed ${closedCount} pub${closedCount === 1 ? "" : "s"} for this Gameweek.${payoutSuffix}${blastSuffix} The season remains open for un-selected pubs.`
        );
      } else if (champ?.email && data?.email_sent) {
        toast.success(`${closedName} ending ${closedDate} closed. Champion ${champ.name} emailed.${payoutSuffix}${blastSuffix} Start the next season below.`);
      } else if (champ?.email && !data?.email_sent) {
        toast.warning(`${closedName} ending ${closedDate} closed but email to ${champ.email} failed.${payoutSuffix}${blastSuffix} Start the next season below.`);
      } else if (champ) {
        toast.success(`${closedName} ending ${closedDate} closed. Champion ${champ.name} (no email on file).${payoutSuffix}${blastSuffix}`);
      } else {
        toast.success(`${closedName} ending ${closedDate} closed. No champion found.${payoutSuffix}${blastSuffix}`);
      }
      setConfirmOpen(false);
      // Reset selection so the next open re-fetches (in case pubs
      // changed) and defaults to "all selected" again.
      setPubsInitialised(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const renameSeason = async (e) => {
    e?.preventDefault?.();
    const name = renameValue.trim();
    if (!name || !activeSeason) return;
    setRenaming(true);
    try {
      const { data } = await api.patch(`/pools/seasons/${activeSeason.id}`, { name });
      toast.success(`Season renamed to "${data.season.name}"`);
      setRenameOpen(false);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRenaming(false);
    }
  };

  // Always render — when there's no active season, fall back to an idle
  // "nothing to close" state so the editor sees the same lifecycle UI
  // (Close above Start) regardless of state.
  if (!activeSeason) {
    return (
      <div className={`${SECTION} p-6 opacity-90`} data-testid="close-season-card">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-full bg-zinc-100 text-zinc-400 flex items-center justify-center shrink-0">
            <Trophy className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-lg font-bold tracking-tight text-zinc-950">Close season &amp; finalise prize pot</h3>
            <p className="text-sm text-zinc-500 mt-1 max-w-xl">
              Permanently locks the current end-of-league prize pot for payout, snapshots the final standings,
              and auto-drafts payout rows for the top finishers (per your pool &amp; prize-fund split) in the Manual
              Payouts card.
            </p>
            <div
              className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-zinc-300 bg-zinc-50 text-sm text-zinc-600"
              data-testid="close-season-idle"
            >
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">No open season</span>
              <span>Nothing to close right now — start one below.</span>
            </div>
            <div className="mt-4">
              <Button
                disabled
                data-testid="close-season-btn"
                variant="outline"
                className="h-11 rounded-sm border-zinc-300 text-zinc-400 cursor-not-allowed"
              >
                Close current season
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${SECTION} p-6`} data-testid="close-season-card">
      <div className="flex items-start gap-4">
        <div className="h-10 w-10 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
          <Trophy className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-lg font-bold tracking-tight text-zinc-950">Close season &amp; finalise prize pot</h3>
          <p className="text-sm text-zinc-500 mt-1 max-w-xl">
            Permanently locks the current end-of-league prize pot for payout and snapshots the final standings.
            Auto-drafts payout rows for the top finishers (per your pool &amp; prize-fund split) in the Manual
            Payouts card. A new season is <strong>not</strong> started automatically.
          </p>

          {/* Active-season status row */}
          <div className="mt-4 inline-flex items-center gap-3 flex-wrap" data-testid="active-season-marker">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Open season</span>
            <strong className="text-sm text-zinc-950" data-testid="active-season-name">{activeSeason.name}</strong>
            {!renameOpen && (
              <button
                type="button"
                onClick={() => { setRenameValue(activeSeason.name); setRenameOpen(true); }}
                data-testid="rename-season-btn"
                className="text-xs font-semibold text-[#0038FF] hover:text-blue-800"
              >
                Rename
              </button>
            )}
            {renameOpen && (
              <form onSubmit={renameSeason} className="inline-flex items-center gap-2">
                <Input
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  data-testid="rename-season-input"
                  className="h-9 w-56 rounded-sm border-zinc-300 text-sm"
                  autoFocus
                />
                <Button type="submit" disabled={renaming} data-testid="rename-season-save" className="h-9 rounded-sm bg-zinc-950 text-white text-xs">
                  {renaming ? "Saving…" : "Save"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setRenameOpen(false)} disabled={renaming} className="h-9 rounded-sm text-xs">
                  Cancel
                </Button>
              </form>
            )}
          </div>

          <div className="mt-4">
            <Button
              onClick={() => setConfirmOpen(true)}
              data-testid="close-season-btn"
              variant="outline"
              className="h-11 rounded-sm border-amber-400 text-amber-700 hover:bg-amber-50"
            >
              Close current season
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="close-season-confirm" className="sm:max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <DialogTitle className="font-heading text-2xl">Are you sure?</DialogTitle>
            </div>
            <DialogDescription asChild>
              <div className="text-sm text-zinc-600 mt-2 space-y-3">
                <p>
                  Closing the season is final. Payout rows for the top finishers (according to each pool &amp; prize-fund
                  split) will be drafted into <strong>Manual payouts</strong>, every predictor gets a personal recap
                  email, and each pub&apos;s Gameweek counter ticks over.
                </p>
                {isMaster && (
                  <div
                    className="rounded-sm border border-zinc-200 bg-zinc-50 p-3"
                    data-testid="close-season-pub-picker"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-600">
                        Close for which pubs?
                      </p>
                      {pubs.length > 0 && (
                        <button
                          type="button"
                          onClick={toggleAll}
                          disabled={pubsLoading || busy}
                          data-testid="close-season-toggle-all"
                          className="text-xs font-semibold text-[#0038FF] hover:text-blue-800"
                        >
                          {allSelected ? "Deselect all" : "Select all"}
                        </button>
                      )}
                    </div>
                    {pubsLoading && (
                      <p className="text-xs text-zinc-500 py-2" data-testid="close-season-pubs-loading">
                        Loading pubs…
                      </p>
                    )}
                    {!pubsLoading && pubs.length === 0 && (
                      <p className="text-xs text-zinc-500 py-2">No pubs found — the close will run on the legacy cohort only.</p>
                    )}
                    {!pubsLoading && pubs.length > 0 && (
                      <ul className="max-h-56 overflow-y-auto space-y-1.5 pr-1" data-testid="close-season-pubs-list">
                        {pubs.map((p) => {
                          const checked = selectedPubIds.has(p.id);
                          const readiness = readinessByPubId[p.id];
                          const unfinished = readiness?.unfinished_count || 0;
                          return (
                            <li key={p.id}>
                              <label
                                data-testid={`close-season-pub-${p.slug}`}
                                className="flex items-center gap-3 py-1 cursor-pointer text-sm text-zinc-800 hover:text-zinc-950"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => togglePub(p.id)}
                                  disabled={busy}
                                  className="h-4 w-4 rounded-sm border-zinc-300 text-[#0038FF] focus:ring-[#0038FF]/40"
                                />
                                <span className="flex-1 min-w-0 font-medium truncate">{p.name}</span>
                                {unfinished > 0 && (
                                  <span
                                    data-testid={`close-season-unfinished-${p.slug}`}
                                    title={`${unfinished} of ${readiness?.total_count ?? unfinished} fixtures haven't finished yet`}
                                    className="inline-flex items-center gap-1 shrink-0 rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                                  >
                                    <AlertTriangle className="h-3 w-3" />
                                    {unfinished} unfinished
                                  </span>
                                )}
                                <code className="text-[11px] font-mono text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded-sm shrink-0">{p.slug}</code>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    {!pubsLoading && tickedUnfinishedCount > 0 && (
                      <div
                        data-testid="close-season-unfinished-warning"
                        className="mt-3 rounded-sm border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2"
                      >
                        <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0 text-[12px] text-amber-900 leading-relaxed">
                          <strong>{tickedUnfinishedCount}</strong> of the ticked pub{tickedUnfinishedCount === 1 ? "" : "s"} still {tickedUnfinishedCount === 1 ? "has" : "have"} unfinished fixtures — closing now will lock those matches out mid-Gameweek.
                        </div>
                        <button
                          type="button"
                          onClick={uncheckUnfinished}
                          disabled={busy}
                          data-testid="close-season-untick-unfinished"
                          className="text-[11px] font-semibold text-amber-900 underline hover:text-amber-950 shrink-0"
                        >
                          Untick them
                        </button>
                      </div>
                    )}
                    {!pubsLoading && pubs.length > 0 && (
                      <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed" data-testid="close-season-scope-hint">
                        {allSelected ? (
                          <>Every pub will close and the season will flip to <strong>closed</strong> — start a new season below afterwards.</>
                        ) : noneSelected ? (
                          <span className="text-red-700">Tick at least one pub to continue.</span>
                        ) : (
                          <>Only the <strong>{selectedPubIds.size}</strong> ticked pub{selectedPubIds.size === 1 ? "" : "s"} will close. The season stays active so un-ticked pubs keep predicting.</>
                        )}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={busy} className="h-11 rounded-sm">
              Cancel
            </Button>
            <Button
              onClick={close}
              disabled={busy || pubsLoading || noneSelected}
              data-testid="close-season-confirm-btn"
              className="h-11 rounded-sm bg-amber-600 hover:bg-amber-700 text-white"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isMaster && !allSelected && !noneSelected ? (
                `Close ${selectedPubIds.size} pub${selectedPubIds.size === 1 ? "" : "s"}`
              ) : (
                "Yes, close the season"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Start-season card — ONLY shows when there's NO active season.
// ---------------------------------------------------------------------------
function StartSeasonCard({ activeSeason, lastClosure, onChanged }) {
  const [newSeasonName, setNewSeasonName] = useState("");
  const [starting, setStarting] = useState(false);

  const startSeason = async (e) => {
    e?.preventDefault?.();
    const name = newSeasonName.trim();
    if (!name) {
      toast.error("Give the new season a name first");
      return;
    }
    setStarting(true);
    try {
      const { data } = await api.post("/pools/start-season", { name });
      toast.success(`Season "${data.season.name}" is now active`);
      setNewSeasonName("");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setStarting(false);
    }
  };

  const closedDateLabel = lastClosure?.closed_at
    ? new Date(lastClosure.closed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : null;

  // When an active season already exists, render an idle disabled state
  // (instead of hiding entirely) so the editor always sees the same
  // Close → Start vertical layout.
  if (activeSeason) {
    return (
      <div className={`${SECTION} p-6 opacity-90`} data-testid="start-season-card">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-full bg-zinc-100 text-zinc-400 flex items-center justify-center shrink-0">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-lg font-bold tracking-tight text-zinc-950">Start the next season</h3>
            <p className="text-sm text-zinc-500 mt-1 max-w-xl">
              Open the next season once you&apos;ve closed the current one. You&apos;ll choose its name (e.g. &quot;Premier League 2026/27&quot;, &quot;Champions League 2026/27&quot;) before predictions can resume.
            </p>
            <div
              className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-zinc-300 bg-zinc-50 text-sm text-zinc-600"
              data-testid="start-season-idle"
            >
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Currently open</span>
              <strong className="text-zinc-950">{activeSeason.name}</strong>
              <span>— close it above first.</span>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <Input
                value=""
                disabled
                placeholder="Close the current season to enable this"
                data-testid="start-season-input"
                className="h-11 w-72 rounded-sm border-zinc-300 bg-zinc-50"
              />
              <Button
                disabled
                data-testid="start-season-btn"
                className="h-11 rounded-sm bg-zinc-200 text-zinc-400 cursor-not-allowed hover:bg-zinc-200"
              >
                Start new season
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${SECTION} p-6`} data-testid="start-season-card">
      <div className="flex items-start gap-4">
        <div className="h-10 w-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-lg font-bold tracking-tight text-zinc-950">Start the next season</h3>
          <p className="text-sm text-zinc-500 mt-1 max-w-xl">
            No season is currently active. Give the next one a name (e.g. &quot;Premier
            League 2026/27&quot;) and open it for predictions.
          </p>
          {lastClosure && (
            <div
              data-testid="last-closure-marker"
              className="mt-4 inline-flex items-start gap-2 px-3 py-2 rounded-sm border border-emerald-300 bg-emerald-50 text-sm text-emerald-900"
            >
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                <strong>{lastClosure.season_name || "Season"}</strong>
                {closedDateLabel ? <> closed on <strong>{closedDateLabel}</strong></> : null}
                {(lastClosure.champion?.name) && (
                  <> · champion: <strong>{lastClosure.champion.name}</strong>.</>
                )}
              </span>
            </div>
          )}
          <form onSubmit={startSeason} className="mt-4 flex flex-col gap-2 max-w-md" data-testid="start-season-form">
            <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
              Season name
            </Label>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                value={newSeasonName}
                onChange={(e) => setNewSeasonName(e.target.value)}
                placeholder="e.g. Premier League 2026/27, Champions League 2026/27…"
                data-testid="start-season-input"
                disabled={starting}
                className="h-11 w-72 rounded-sm border-zinc-300"
              />
              <Button
                type="submit"
                disabled={starting || !newSeasonName.trim()}
                data-testid="start-season-btn"
                className="h-11 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Start new season"}
              </Button>
            </div>
            <p className="text-xs text-zinc-500">
              Once started, any new matches you create will be assigned to this season and its leaderboard.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Combined wrapper — loads the active season + most-recent closure once and
// renders whichever of the two cards is appropriate.
// ---------------------------------------------------------------------------
function SeasonLifecycleSection({ slug = null }) {
  const [activeSeason, setActiveSeason] = useState(null);
  const [lastClosure, setLastClosure] = useState(null);

  const reload = useCallback(async () => {
    try {
      const [a, c] = await Promise.all([
        api.get("/seasons/current").catch(() => ({ data: null })),
        api.get("/admin/season-closures").catch(() => ({ data: [] })),
      ]);
      setActiveSeason(a.data || null);
      setLastClosure(Array.isArray(c.data) && c.data.length > 0 ? c.data[0] : null);
    } catch (err) {
      // Non-fatal — season lifecycle cards render an empty state on failure.
      if (typeof console !== "undefined") console.debug("[editor] season lifecycle reload failed:", err?.message);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="space-y-6" data-testid="season-lifecycle-section">
      <CloseSeasonCard activeSeason={activeSeason} onChanged={reload} slug={slug} />
      <StartSeasonCard activeSeason={activeSeason} lastClosure={lastClosure} onChanged={reload} />
    </div>
  );
}

export default function EditorPage() {
  // Single entry point for BOTH `/editor` (master, Dugsy-wide) and
  // `/c/[slug]/editor` (client-scoped). Data + writes are scoped to the pub
  // via the `?as_pub=<slug>` interceptor in `src/lib/api.js` — the same
  // layout, header, and typography are used in both cases so super-admins
  // and pub owners get an identical experience. When impersonating a pub
  // (Super-Admin visiting `/c/[slug]/editor`), a yellow banner reminds
  // them every write lands on that pub's record.
  const { slug } = useParams();
  const { user } = useAuth();
  // iter88 — only show the "Super-Admin view — managing X on behalf of the
  // client" banner when the caller is genuinely impersonating (admin OR
  // is_editor). Native client-role users at /c/[slug]/editor are seeing
  // their own record, so the copy is misleading and must be suppressed.
  const showImpersonationBanner = !!slug && (user?.role === "admin" || !!user?.is_editor);

  // iter134 — per-pub sponsorship editing for staff editors (admin OR
  // is_editor) at /c/[slug]/editor. `slug` is a *client* slug in this
  // context, so we fetch that client's pubs via `?as_client=<slug>`
  // impersonation and render a `LocalSponsorsCard` per-pub. This restores
  // the ability lost in iter133 (where we correctly locked the platform-
  // wide global sponsorship down to super-admin only). Writes hit the
  // existing PUT /clients/me/pubs/{id} endpoint which is already scoped
  // by client and therefore cannot leak across slugs.
  const canEditSlug = !!slug && (user?.role === "admin" || !!user?.is_editor);
  const clientScope = useMemo(() => {
    return (path) => {
      if (!canEditSlug) return path;
      const sep = path.includes("?") ? "&" : "?";
      return `${path}${sep}as_client=${encodeURIComponent(slug)}`;
    };
  }, [canEditSlug, slug]);
  const [slugPubs, setSlugPubs] = useState([]);
  const loadSlugPubs = useCallback(async () => {
    if (!canEditSlug) return;
    try {
      const { data } = await api.get(clientScope("/clients/me/pubs"));
      setSlugPubs(Array.isArray(data) ? data : []);
    } catch (_e) {
      // Silent — this section is a bonus, don't break the editor if the
      // slug isn't a client slug (e.g. legacy single-pub setups).
      setSlugPubs([]);
    }
  }, [canEditSlug, clientScope]);
  useEffect(() => {
    loadSlugPubs();
  }, [loadSlugPubs]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24" data-testid="editor-dashboard">
        {showImpersonationBanner && (
          <div
            className="mb-6 rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex flex-wrap items-center gap-3"
            data-testid="editor-impersonation-banner"
          >
            <ShieldCheck className="h-4 w-4 shrink-0" />
            <span className="flex-1 min-w-0">
              {user?.role === "admin" ? "Super-Admin" : "Editor"} view — managing <code className="px-1 bg-amber-100 rounded-sm text-[12px]">{slug}</code> on behalf of the client. Every change is written to their record.
            </span>
            {/* iter139 — Super-Admin only: a client-side (no reload) link
                back to the Client Manager. Fixes a UX gap where the only
                way out of a per-slug editor was the browser back button,
                which some users found landed them at the login screen. */}
            {user?.role === "admin" && (
              <Link
                to="/editor/clients"
                data-testid="editor-back-to-client-manager"
                className="inline-flex items-center gap-1.5 h-8 rounded-sm bg-amber-900 hover:bg-amber-950 text-white text-xs font-semibold px-3 shrink-0"
              >
                <span aria-hidden="true">&larr;</span> Back to Client Manager
              </Link>
            )}
          </div>
        )}
        <div className="mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5" /> Editor
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950 mt-1">
            Editor&apos;s Dashboard.
          </h1>
          <p className="text-sm text-zinc-500 mt-3 max-w-2xl">
            {canEditSlug ? (
              <>Track engagement, manage this pub&apos;s prize pool, and reschedule match-days. Paid entry is processed by Stripe — manage winner payouts in the Manual payouts panel below.</>
            ) : (
              <>Track engagement across every tenant, reschedule match-days platform-wide, and manage each pub&apos;s prize pool from its own <span className="font-mono">/c/&lt;slug&gt;/editor</span> page.</>
            )}
          </p>
          {/* iter151 — Gameweek banner on the branded editor too, so
              super-admins can see the current gameweek/match/day at a
              glance without hopping to Predictions. Renders null when
              the slug has no gameweek engine fields set. */}
          {canEditSlug && (
            <div className="mt-4">
              <GameweekBanner variant="stacked" />
            </div>
          )}
        </div>

        <StatsDashboard />

        {/* iter137 — Pool & Prize is now per-pub. The section only
            shows when scoped to a slug (`/c/<slug>/editor`); on the
            master `/editor` route we hide it entirely so a super-admin
            can't accidentally clobber the global defaults. Each pub is
            managed independently via `/c/<slug>/editor`. */}
        {canEditSlug && (
          <div className="mt-12">
            <PoolAndPrizeSection />
          </div>
        )}

        {user?.role === "admin" && (
          <div className="mt-12">
            <RegistrationSponsorshipCard />
          </div>
        )}

        {canEditSlug && slugPubs.length > 0 && (
          <div className="mt-12 space-y-4" data-testid="editor-slug-sponsors-section">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-2">
                Local sponsors
              </p>
              <h2 className="font-heading text-2xl font-bold text-zinc-950 mt-1">
                Sponsorship for {slug}
              </h2>
              <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
                Per-pub sponsor tiles and message. Only editors scoped to
                this client can change these values — they show on the
                branded login / register pages for the pubs below.
              </p>
            </div>
            {slugPubs.map((p) => (
              <LocalSponsorsCard
                key={p.id}
                pub={p}
                scope={clientScope}
                onSaved={loadSlugPubs}
              />
            ))}
          </div>
        )}

        {canEditSlug && slugPubs.length > 0 && (
          <div className="mt-12 space-y-4" data-testid="editor-slug-whatsapp-section">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-2">
                WhatsApp banter group
              </p>
              <h2 className="font-heading text-2xl font-bold text-zinc-950 mt-1">
                WhatsApp invite for {slug}
              </h2>
              <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
                Per-pub WhatsApp group URL. When a user ticks &quot;Add
                me to the WhatsApp group&quot; on this pub&apos;s
                registration page, they receive an email with THIS link.
              </p>
            </div>
            {slugPubs.map((p) => (
              <LocalWhatsAppCard
                key={p.id}
                pub={p}
                scope={clientScope}
                onSaved={loadSlugPubs}
              />
            ))}
          </div>
        )}


        <div className="mt-12">
          <EditorResultsCard />
        </div>

        <div className="mt-12">
          <BroadcastPanel />
        </div>

        <div className="mt-12">
          <SeasonLifecycleSection slug={slug} />
        </div>

        <div className="mt-12">
          <BulkEmailCard />
        </div>

        <div className="mt-12">
          <EmailBlockAuditCard />
        </div>

        <div className="mt-12">
          <ManualPayoutsCard />
        </div>

        <div className="mt-12">
          <WhatsAppOptInsCard />
        </div>

        <PlayersCard
          mode={slug ? "client-scoped" : "admin"}
          scopeSlug={slug || null}
        />
      </main>
    </div>
  );
}
